import React, { useEffect, useRef, useState } from 'react';
import { View, KeyboardAvoidingView, Pressable, useWindowDimensions, Modal, ScrollView, Platform, Keyboard, Image, TouchableOpacity, BackHandler } from 'react-native';
import { Theme, ThemeConsumer } from "@rneui/themed";
import { useFormik } from 'formik';
import * as yup from 'yup';
import Popover from 'react-native-popover-view';

import CommonService from '../../../shared/services/commonService';
import { config } from '../../../shared/environment';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';

import CommonHeader from '../../../shared/commonComponents/commonHeader';
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox';

import { countriesDropdownProps, dropDownProps, stateDropDownProps, valuesProps, updateStoreDetailsProps } from './storeSettingModule';
import { stateApi } from '../../customers/addEditCustomerModule';

import { posStoreDetails, updateStore } from '../../../redux/slices/storeSettingSlice';
import { currencies, industryTypesName, loginState, updateCurrentUser } from '../../../redux/slices/commonSlice';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../../redux/hooks';

import { ZenButton, ZenDropdown, ZenIcon, ZenInput, ZenLoader, ZenText, ZenToast } from 'zen-core-elements';
import { ZenRBSheetRef } from 'zen-core-elements/src/elements/zenRBSheet/RBSheet';
import { CustomModal } from '../../../shared/commonComponents/customModal';
import CommonImageUpload, { commonPickerRes } from '../../../shared/commonComponents/commonImageUpload';
import { shallowEqual } from 'react-redux';

type SettingsDetailsHeader = "General Details" | "Address Details" | "Social Media Details"
type DropdownOpenType = "industryVal" | "country" | "state" | "currencyVal" | ""

function StoreSetting(): React.JSX.Element {
    const { width } = useWindowDimensions();
    const dispatch = useAppDispatch()
    const { isWrite: isSettingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual)
    const [transparentLoader, setTransparentLoader] = useState(false)
    const posCountries = useAppSelector((state) => state?.common?.getCountry?.value?.country)
    const { value: currentUser } = useAppSelector((state) => state?.common?.currentUser)
    const bottomRef = useRef<ZenRBSheetRef>(null)
    const [removeImagePopoverVisible, setRemoveImagePopoverVisible] = useState(false)
    //Variable to handle collapse header 
    const navigation = useAppNavigation()
    const [loading, setLoading] = useState<boolean>(true)
    const [collapseHeader, setCollapseHeader] = useState<SettingsDetailsHeader[]>(["General Details", "Address Details", "Social Media Details"])
    //variable to maintain the keyboard open and close
    const [keyboardOpen, setKeyboardOpen] = useState(false)
    //Variable to handle input container width
    const [inputWidth, setInputWidth] = useState<"100%" | "50%">('100%')
    //variable to handle transparent loader
    const { storeDetails, error } = useAppSelector((state) => state?.StoreSetting)
    //variable to handle leave popover
    const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)

    const [countries, setCountries] = useState<countriesDropdownProps[]>([])
    const [stateValues, setStateValues] = useState<stateDropDownProps[] | any>([])
    const [industryType, setIndustryType] = useState<dropDownProps[] | any>([])
    const [storeCurrency, setStoreCurrency] = useState<dropDownProps[] | any>([])
    const [dropdownOpen, setDropdownOpen] = useState<DropdownOpenType>("")

    //useEffect triggered  when dimention(width) change 
    useEffect(() => {
        if (width > 600) {
            setInputWidth("50%")
        }
        else {
            setInputWidth("100%")
        }
    }, [width])

    //UseEffect for handle keyboard
    useEffect(() => {
        Keyboard.addListener("keyboardDidShow", () => {
            setKeyboardOpen(true);
        });
        Keyboard.addListener("keyboardDidHide", () => {
            setKeyboardOpen(false);
        });
    }, [])

    useEffect(() => {
        getCountries()
    }, [])

    const getCountries = () => {
        let temp = posCountries?.reduce((arr: countriesDropdownProps[], item) => (
            [...arr, { label: item?.name, value: item?.name, code: item?.countryCode }]
        ), [])
        setCountries(temp!!)
        getIndustry()
    }

    const getIndustry = async () => {
        await dispatch(industryTypesName()).unwrap().then(async (res) => {
            if (res) {
                let temp = res?.industries?.reduce((arr: dropDownProps[], item) => (
                    [...arr, { label: item?.name, value: item?.id }]
                ), [])
                setIndustryType(temp)
                await dispatch(currencies()).unwrap().then((key) => {
                    if (key) {
                        let tempArray = key?.currencies?.reduce((arr: dropDownProps[], item) => (
                            [...arr, { label: item?.currency_code + ' - (' + item?.currency_symbol + ')', value: item?.id }]
                        ), [])
                        setStoreCurrency(tempArray)
                        fetchPosStoreDetails()
                    }
                }).catch((error: string) => {
                    console.log("getAllPosCurrencies error", error)
                })
            }
        }).catch((err: string) => {
            console.log('error', err)
        })
    }

    useEffect(() => {
        if (error) {
            ZenToast({
                type: "error",
                message: "Error while fetching store details"
            })
        }
    }, [error])

    const fetchPosStoreDetails = async () => {
        await dispatch(posStoreDetails({ storeId: config.storeId }))?.then((response) => {
            if (response) {
                setLoading(false)
            }
        })
    }

    //method for form validation to get store details
    const validationSchema = yup.object().shape({
        name: yup.string()
            .label('name')
            .required('Store Name is required!')
            .max(30, 'Store Name exceeds Maximum length!'),
        contactNumber: yup.string()
            .label('contactNo')
            .required('Contact Number is required')
            .max(20, ('Contact Number should not exceed ' + 20 + ' characters.'))
            .matches(/^[0-9]+$/, 'Contact Number is invalid'),
        email: yup.string()
            .label('email')
            .email('Please provide a valid email address')
            .matches(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,3}))$/, 'Please provide a valid email address')
            .required('Email is required'),
        faceBook: yup.string()
            .test('Validation', 'Please enter a valid link',
                (value) => {
                    let temp
                    if (value) {
                        temp = (value == value?.trim()) ? true : false
                        return temp
                    }
                    else {
                        return true
                    }
                }
            ),
        instagram: yup.string()
            .test('Validation', 'Please enter a valid link',
                (value) => {
                    let temp
                    if (value) {
                        temp = (value == value?.trim()) ? true : false
                        return temp
                    }
                    else {
                        return true
                    }
                }
            ),
        linkedIn: yup.string()
            .test('Validation', 'Please enter a valid link',
                (value) => {
                    let temp
                    if (value) {
                        temp = (value == value?.trim()) ? true : false
                        return temp
                    }
                    else {
                        return true
                    }
                }
            ),
        pinterest: yup.string()
            .test('Validation', 'Please enter a valid link',
                (value) => {
                    let temp
                    if (value) {
                        temp = (value == value?.trim()) ? true : false
                        return temp
                    }
                    else {
                        return true
                    }
                }
            ),
        twitter: yup.string()
            .test('Validation', 'Please enter a valid link',
                (value) => {
                    let temp
                    if (value) {
                        temp = (value == value?.trim()) ? true : false
                        return temp
                    }
                    else {
                        return true
                    }
                }
            ),
        youtube: yup.string()
            .test('Validation', 'Please enter a valid link',
                (value) => {
                    let temp
                    if (value) {
                        temp = (value == value?.trim()) ? true : false
                        return temp
                    }
                    else {
                        return true
                    }
                }
            ),
        addressLine1: yup.string()
            .required('Address is required')
            .max(100, 'Address should not exceed 100 characters'),
        addressLine2: yup.string().max(100, 'Address should not exceed 100 characters'),
        city: yup.string()
            .required('City is required')
            .max(100, 'City should not exceed 100 characters'),
        country: yup.string()
            .required('Country is required'),
        state: yup.string()
            .required('State is required'),
        zipCode: yup.string()
            // .matches((countryId !== 'India' ? /^[1-9]\d{4}$/ : /^[1-9]\d{5}$/), (countryId !== 'India' ? 'Zip Code accepts 5 digits only' : 'Zip Code accepts 6 digits only'))
            .required('ZipCode is required')
            .max(15, ('ZipCode should not exceed ' + 15 + ' characters'))
            .matches(/^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]*$/, 'ZipCode is invalid'),
    })
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            logoUrl: storeDetails?.logoUrl ? storeDetails?.logoUrl : '',
            name: storeDetails?.store?.name ? storeDetails?.store?.name : '',
            email: storeDetails?.email ? storeDetails?.email : '',
            contactNumber: storeDetails?.contactNo ? storeDetails?.contactNo.toString() : '',
            industryVal: storeDetails?.store?.industryId ? storeDetails?.store?.industryId : '',
            websiteUrl: storeDetails?.store?.websiteUrl ? storeDetails?.store?.websiteUrl : '',
            currencyVal: storeDetails?.currency?.id ? storeDetails?.currency?.id : '',
            contactName: storeDetails?.contactName ? storeDetails?.contactName : '',

            addressLine1: storeDetails?.address?.addressLine1 ? storeDetails?.address?.addressLine1 : '',
            addressLine2: storeDetails?.address?.addressLine2 ? storeDetails?.address?.addressLine2 : '',
            country: storeDetails?.countryCode ? countries?.find((item: countriesDropdownProps) => item?.code == storeDetails?.countryCode)?.label!! : '',
            state: storeDetails?.address?.state ? storeDetails?.address?.state : '',
            city: storeDetails?.address?.city ? storeDetails?.address?.city : '',
            zipCode: storeDetails?.address?.zipCode ? storeDetails?.address?.zipCode : '',

            faceBook: storeDetails?.faceBook ? storeDetails?.faceBook : '',
            instagram: storeDetails?.instagram ? storeDetails?.instagram : '',
            linkedIn: storeDetails?.linkedIn ? storeDetails?.linkedIn : '',
            pinterest: storeDetails?.pinterest ? storeDetails?.pinterest : '',
            twitter: storeDetails?.twitter ? storeDetails?.twitter : '',
            youtube: storeDetails?.youtube ? storeDetails?.youtube : '',

            /* --------------- Not use --------------- */
            // displayName: storeDetails?.store?.displayName ? storeDetails?.store?.displayName : '',
            // taxId: storeDetails?.taxId ? storeDetails?.taxId : '',
            // domainName: storeDetails?.domainName ? storeDetails?.domainName : '',
            // isShopClosed: storeDetails?.isShopClosed ? storeDetails?.isShopClosed : false,
            // siteUnderConstructionMessage: storeDetails?.siteUnderConstructionMessage ? storeDetails?.siteUnderConstructionMessage : '',
            // seoKeyword: storeDetails?.seoKeyword ? storeDetails?.seoKeyword : '',
            // seodescription: storeDetails?.seodescription ? storeDetails?.seodescription : '',
        },
        validationSchema: validationSchema,
        onSubmit: (values: valuesProps) => {
            if (formik?.dirty) {
                updateStoreDetails(values)
            }
            else {
                ZenToast({
                    type: "info",
                    message: "You have no changes to save"
                })
            }
        }
    })

    //UseEffect for handle Hardware BackPress
    useEffect(() => {
        const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
            if (formik?.dirty) {
                setLeavePopoverVisible(true)
                return true
            }
            else {
                navigation.goBack()
            }
            return true
        });
        return () => backHandler?.remove()
    }, [formik?.values])

    useEffect(() => {
        if (formik?.values?.country) {
            getPosStatesWithCountryName(formik?.values?.country as string)
        }
    }, [formik?.values?.country])

    const getPosStatesWithCountryName = async (CountryName: string) => {
        await CommonService?.getStateName(CountryName).then((response: { states: stateApi[] }) => {
            if (response) {
                let temp = response?.states?.reduce((arr: stateDropDownProps[], item) => (
                    [...arr, { label: item?.name, value: item?.name }]
                ), [])
                setStateValues(temp)
            }
        }).catch((error: string) => {
            console.log("getPosStatesWithCountryName error", error)

        })
    }

    //Function used to handle the image picker actions
    const handleImage = (name: string, res?: commonPickerRes[]) => {
        switch (name) {
            case 'Camera':
            case 'Library':
                if (res && res.length > 0) {
                    formik?.setFieldValue('logoUrl', res?.[0]?.key!)
                }
                break;
            case 'StoreImageGallery':
                if (res && res.length > 0) {
                    formik?.setFieldValue('logoUrl', res?.[0]?.key!)
                }
                break;
            case 'Cancel':
                break;
        }
    }

    //method to update store details
    const updateStoreDetails = async (values: valuesProps) => {
        setTransparentLoader(true)
        // merge into the stored address so keys this form doesn't edit (stateId, countryId, timezone…) are not wiped
        let addressObj = JSON.stringify({ ...(storeDetails?.address ?? {}), addressLine1: values?.addressLine1, addressLine2: values?.addressLine2, city: values?.city, country: values?.country, state: values?.state, zipCode: values?.zipCode })

        const data: updateStoreDetailsProps = {
            ...formik?.values?.logoUrl && { logoImage: formik?.values?.logoUrl },
            deletedLogoUrl: storeDetails?.logoUrl !== formik?.values?.logoUrl ? storeDetails?.logoUrl ? "settings" : null : null,
            deletedLogoName: storeDetails?.logoUrl !== formik?.values?.logoUrl ? storeDetails?.logoUrl?.split("/").slice(-1).toString() ?? null : null,
            name: values?.name ? values?.name : '',
            websiteUrl: values?.websiteUrl ? values?.websiteUrl : '',
            industryType: values.industryVal ? values?.industryVal : '',
            currencyId: values?.currencyVal ? values?.currencyVal : null,
            currencyUnit: values?.currencyVal ? values?.currencyVal : null,

            address: addressObj || null,
            faceBook: values?.faceBook ? values?.faceBook : null,
            instagram: values?.instagram ? values?.instagram : null,
            linkedIn: values?.linkedIn ? values?.linkedIn : null,
            pinterest: values?.pinterest ? values?.pinterest : null,
            twitter: values?.twitter ? values?.twitter : null,
            youtube: values?.youtube ? values?.youtube : null,

            userId: currentUser?.userId!!,
            clientId: currentUser?.clientId!!

            /* --------------- Disable field --------------- */
            // email: values?.email ? values?.email : '',
            // contactName: values?.contactName ? values?.contactName : null,

            /* --------------- Not use --------------- */
            // taxId: values?.taxId ? values?.taxId : '',
            // domainName: values?.domainName ? values?.domainName : null,
            // displayName: values?.displayName ? values?.displayName : '',
            // seodescription: values?.seodescription ? values?.seodescription : '',
            // seoKeyword: values?.seoKeyword ? values?.seoKeyword : '',
            // siteUnderConstructionMessage: values?.siteUnderConstructionMessage ? values?.siteUnderConstructionMessage : null,
        }
        await dispatch(updateStore({ storeId: config?.storeId, data })).unwrap().then((res) => {
            if (res) {
                ZenToast({
                    type: "success",
                    message: "Store details updated successfully"
                })

                setTransparentLoader(false)
                const updatedSymbol = storeCurrency?.find((curr: any) => curr?.value === formik?.values?.currencyVal)?.label?.split("-")?.[1]?.replace(/[()]/g, "").trim()

                dispatch(updateCurrentUser({ ...currentUser, purchasedCurrency: { ...currentUser?.purchasedCurrency, currencySymbol: updatedSymbol, } }))
                navigation.goBack()
            }
        }).catch((err: string) => {
            ZenToast({
                type: "error",
                message: "Failed to update store details update"
            })
            setTransparentLoader(false)
            console.log('Error in update Store details', err)
        })
    }

    //Method for handle collapse header state
    const collapseFunction = (header: SettingsDetailsHeader) => {
        setCollapseHeader((prev) => {
            const index = prev.indexOf(header);
            return index >= 0 ? prev.filter(item => item !== header) : [...prev, header];
        });
    }

    //Method for handle dropdown open/close state
    const openDropdown = (formikName: DropdownOpenType) => {
        setDropdownOpen((formikName == dropdownOpen) ? "" : formikName)
    }

    //method for popover confirmation 
    const confirmation = (item: { id: number }) => {
        setLeavePopoverVisible(false)
        if (item?.id == 2) {
            navigation?.goBack()
        }
    }
    const removeImageConfirmation = (item: { id: number }) => {
        setRemoveImagePopoverVisible(false)
        if (item?.id == 2) {
            formik?.setFieldValue("logoUrl", null)
        }
    }

    return (
        <ThemeConsumer>
            {({ theme }: { theme: Theme }) => (
                <KeyboardAvoidingView style={theme?.storeSettingsStyle?.containerStyle}>
                    <CommonHeader headerName='Store Settings' backIcon backIconOnpress={() => { formik?.dirty ? setLeavePopoverVisible(true) : navigation?.goBack() }}
                        rightComponent={isSettingWrite ? <ZenButton
                            title={"Save"}
                            titleColor={theme?.light}
                            buttonStyle={{ backgroundColor: theme?.primary, borderRadius: 10, paddingHorizontal: 12 }}
                            onPress={() => {
                                formik.handleSubmit()
                                if (!formik?.isValid) {
                                    ZenToast({
                                        type: "warning",
                                        message: "Please fill out the mandatory fields in store details page"
                                    })
                                }
                            }}
                        /> : undefined}
                    />
                    {
                        loading ?
                            <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                            :
                            <View style={{ flex: 1, marginTop: 10 }}>
                                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={[(Platform.OS === 'ios' && keyboardOpen) ? { paddingBottom: 130 } : null]} keyboardShouldPersistTaps="never">
                                    {/* General Details */}
                                    <View style={[theme?.addEditCustomerStyle?.sectionView, { marginHorizontal: width > 600 ? 15 : 10, zIndex: 10 }]}>
                                        <TouchableOpacity style={[{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", backgroundColor: '#fbfbfb', padding: 10 }, collapseHeader?.indexOf("General Details") < 0 ? { borderRadius: 10 } : { borderTopLeftRadius: 10, borderTopRightRadius: 10 }]} onPress={() => { collapseFunction("General Details") }}>
                                            <View style={{ flexDirection: "row", alignItems: "center", marginLeft: 5 }}>
                                                <ZenIcon type="material-community" name={collapseHeader?.indexOf("General Details") < 0 ? "chevron-up" : "chevron-down"} size={26} color={theme?.primary} />
                                                <ZenText content={"General Details"} size={width > 600 ? "medium" : theme?.smallerFontSize} color={theme.primary} bold style={{ marginHorizontal: 5 }} />
                                            </View>
                                        </TouchableOpacity>
                                        <View style={[theme?.addEditCustomerStyle?.generalSectionView, { justifyContent: "space-between", alignItems: "flex-start", paddingHorizontal: width > 600 ? 10 : 5, display: collapseHeader?.indexOf("General Details") < 0 ? "none" : "flex" }]}>
                                            <View style={{ width: inputWidth, marginBottom: 15 }}>
                                                <ZenText content={"Logo"} color={theme?.labelColor} size={"medium"} style={theme?.addEditCustomerStyle?.dropdownLabelStyle} />
                                                {!formik?.values?.logoUrl ? <Pressable disabled={!isSettingWrite} style={[theme?.receiptSettingsStyle?.logoContainer, width > 600 ? { width: "50%", height: 140, marginBottom: 0 } : { marginBottom: 0, width: "40%", height: 110 }]} onPress={() => { bottomRef?.current?.open() }}>
                                                    <ZenIcon size={width > 600 ? 24 : 22} type="material-community" name="image-plus" color={theme.labelColor} />
                                                    <ZenText content={"Upload Logo"} color={theme?.labelColor} size={width > 600 ? "medium" : theme.smallerFontSize} style={{ paddingTop: 10 }} />
                                                </Pressable> :
                                                    <View style={[{ marginHorizontal: 15 }, width > 600 ? { width: "50%", height: 140 } : { width: "40%", height: 110 }]}>
                                                        <Image
                                                            source={{ uri: config.AWSImageUrl + formik?.values?.logoUrl }}
                                                            style={[{ borderRadius: 10 }, width > 600 ? { width: "100%", height: "100%" } : { width: "100%", height: "100%" }]}
                                                        />
                                                        {isSettingWrite && <Pressable style={[theme?.receiptSettingsStyle?.editIconContainer,]} onPress={() => setRemoveImagePopoverVisible(true)}>
                                                            <ZenIcon size={width > 600 ? 28 : 20} name='close-circle' type='material-community' color={theme.red} />
                                                        </Pressable>}
                                                    </View>
                                                }
                                            </View>
                                            <View style={{ width: inputWidth, marginBottom: formik?.touched.name && formik?.errors?.name ? 10 : 0 }}>
                                                <ZenInput
                                                    label="Store Name *"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.name}
                                                    errorMessage={formik?.touched.name && formik?.errors?.name ? formik?.touched.name && formik?.errors.name : null}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    onChangeText={(text) => { formik?.setFieldValue('name', text) }}
                                                    onBlur={() => { formik?.setFieldValue('name', formik?.values?.name?.trim()); formik?.setFieldTouched('name', true) }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    multiline
                                                    label="Email"
                                                    value={formik?.values?.email}
                                                    disabled={true}
                                                    onChangeText={formik.handleChange('email')}
                                                    onBlur={() => { formik.setFieldTouched('email', true) }}
                                                    errorMessage={formik?.touched.email && formik?.errors.email ? formik?.errors.email : null}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Contact Number"
                                                    disabled={true}
                                                    value={formik?.values?.contactNumber ? (formik?.values?.contactNumber).toString() : ''}
                                                    onChangeText={(text) => {
                                                        formik?.setFieldValue('contactNumber', text)
                                                    }}
                                                    onBlur={() => { formik.setFieldTouched('contactNumber', true) }}
                                                    keyboardType={'phone-pad'}
                                                    errorMessage={formik?.touched.contactNumber && formik?.errors.contactNumber ? formik?.errors.contactNumber : null}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth, marginBottom: 15 }}>
                                                <View style={{ marginHorizontal: 15 }}>
                                                    <ZenDropdown
                                                        title='Industry Type'
                                                        titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                        items={industryType}
                                                        disabled={!isSettingWrite}
                                                        placeholder='Industry Type'
                                                        open={dropdownOpen === "industryVal"}
                                                        setOpen={() => { }}
                                                        onPress={() => {
                                                            openDropdown("industryVal")
                                                        }}
                                                        value={formik?.values?.industryVal}
                                                        onSelectItem={(val) => { formik?.setFieldValue('industryVal', val.value); setDropdownOpen("") }}
                                                        setValue={() => { }}
                                                        autoScroll={true}
                                                        listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                                        labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                                        placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                                    />
                                                </View>
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Website Display Name"
                                                    disabled
                                                    value={formik?.values?.websiteUrl}
                                                    onChangeText={(text) => { formik?.setFieldValue('websiteUrl', text) }}
                                                    onBlur={() => { formik.setFieldTouched('websiteUrl', true) }}
                                                    errorMessage={(formik?.touched.websiteUrl && formik?.errors.websiteUrl) ? formik?.errors.websiteUrl : null}
                                                    errorStyle={{ fontFamily: theme.medium, fontSize: theme.tinyFontSize, marginLeft: 0 }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth, marginBottom: 15 }}>
                                                <View style={{ marginHorizontal: 15 }}>
                                                    <ZenDropdown
                                                        title='Store Currency Unit'
                                                        titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                        items={storeCurrency}
                                                        disabled={!isSettingWrite}
                                                        open={dropdownOpen === "currencyVal"}
                                                        setOpen={() => { }}
                                                        onPress={() => {
                                                            openDropdown("currencyVal")
                                                        }}
                                                        setValue={() => { }}
                                                        value={formik?.values?.currencyVal}
                                                        onSelectItem={(val) => { formik?.setFieldValue('currencyVal', val?.value); setDropdownOpen("") }}
                                                        autoScroll={true}
                                                        listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                                        labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                                        placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                                    />
                                                </View>
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Person Name *"
                                                    value={formik?.values?.contactName}
                                                    disabled={true}
                                                    onBlur={() => { formik.setFieldTouched('contactName', true) }}
                                                    errorMessage={(formik?.touched.contactName && formik?.errors.contactName) ? formik?.errors.contactName : null}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.tinyFontSize, marginLeft: 0 }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>

                                        </View>
                                    </View>
                                    {/* Address Details */}
                                    <View style={[theme?.addEditCustomerStyle?.sectionView, { marginHorizontal: width > 600 ? 15 : 10, zIndex: 9 }]}>
                                        <TouchableOpacity style={[{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", backgroundColor: '#fbfbfb', padding: 10 }, collapseHeader?.indexOf("Address Details") < 0 ? { borderRadius: 10 } : { borderTopLeftRadius: 10, borderTopRightRadius: 10 }]} onPress={() => { collapseFunction("Address Details") }}>
                                            <View style={{ flexDirection: "row", alignItems: "center", marginLeft: 5 }}>
                                                <ZenIcon type="material-community" name={collapseHeader?.indexOf("Address Details") < 0 ? "chevron-up" : "chevron-down"} size={26} color={theme?.primary} />
                                                <ZenText content={"Address Details"} size={width > 600 ? "medium" : theme?.smallerFontSize} color={theme.primary} bold style={{ marginHorizontal: 5 }} />
                                            </View>
                                        </TouchableOpacity>
                                        <View style={[theme?.addEditCustomerStyle?.generalSectionView, { justifyContent: "space-between", alignItems: "flex-start", paddingHorizontal: width > 600 ? 10 : 5, display: collapseHeader?.indexOf("Address Details") < 0 ? "none" : "flex" }]}>
                                            <View style={{ width: inputWidth, marginBottom: formik?.touched.addressLine1 && formik?.errors?.addressLine1 ? 10 : 0 }}>
                                                <ZenInput
                                                    label="AddressLine1 *"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.addressLine1}
                                                    onChangeText={(text: string) => { formik?.setFieldValue('addressLine1', text) }}
                                                    onBlur={() => { formik.setFieldTouched('addressLine1', true); formik?.setFieldValue('addressLine1', (formik?.values?.addressLine1)?.trim()) }}
                                                    errorMessage={(formik?.touched.addressLine1 && formik?.errors.addressLine1) ? formik?.errors.addressLine1 : null}
                                                    errorStyle={{ fontFamily: theme.medium, fontSize: theme.tinyFontSize, color: theme.red, marginLeft: 0 }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="AddressLine2"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.addressLine2}
                                                    onChangeText={(text: string) => { formik?.setFieldValue('addressLine2', text) }}
                                                    onBlur={() => { formik.setFieldTouched('addressLine2', true); formik?.setFieldValue('addressLine2', (formik?.values?.addressLine2)?.trim()) }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <View style={{ marginHorizontal: 15 }}>
                                                    <ZenDropdown
                                                        title='Country *'
                                                        titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                        items={countries}
                                                        open={dropdownOpen === "country"}
                                                        disabled={true}
                                                        setOpen={() => { }}
                                                        onPress={() => {
                                                            openDropdown("country")
                                                        }}
                                                        setValue={() => { }}
                                                        value={formik?.values?.country}
                                                        onSelectItem={(val) => { formik?.setFieldValue('country', val?.value); setDropdownOpen("") }}
                                                        searchable={true}
                                                        autoScroll={true}
                                                        listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                                        labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                                        placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                                    />
                                                </View>
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <View style={[{ marginHorizontal: 15, }, width > 600 ? { marginBottom: 15 } : { marginVertical: 15 }]}>
                                                    <ZenDropdown
                                                        title='State *'
                                                        titleStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                        items={stateValues}
                                                        disabled={!isSettingWrite}
                                                        open={dropdownOpen === "state"}
                                                        setOpen={() => { }}
                                                        onPress={() => {
                                                            openDropdown("state")
                                                        }}
                                                        autoScroll={true}
                                                        setValue={() => { }}
                                                        value={formik?.values?.state}
                                                        onSelectItem={(item) => { formik?.setFieldValue('state', item?.value); setDropdownOpen("") }}
                                                        searchable={true}
                                                        listItemLabelStyle={{ fontSize: theme?.smallerFontSize }}
                                                        labelStyle={{ fontSize: theme?.smallerFontSize, color: theme?.dark, fontFamily: theme?.medium }}
                                                        placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
                                                    />
                                                </View>
                                            </View>
                                            <View style={{ width: inputWidth, marginBottom: formik?.touched.city && formik?.errors?.city ? 10 : 0 }}>
                                                <ZenInput
                                                    label="City *"
                                                    value={formik?.values?.city}
                                                    disabled={!isSettingWrite}
                                                    onChangeText={(text: string) => { formik?.setFieldValue('city', text) }}
                                                    onBlur={() => { formik.setFieldTouched('city', true); formik?.setFieldValue('city', (formik?.values?.city)?.trim()) }}
                                                    errorMessage={(formik?.touched.city && formik?.errors.city) ? formik?.errors.city : null}
                                                    errorStyle={{ fontFamily: theme.medium, fontSize: theme.tinyFontSize, color: theme.red, marginLeft: 0 }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth, marginBottom: formik?.touched.zipCode && formik?.errors?.zipCode ? 10 : 0 }}>
                                                <ZenInput
                                                    label="Zipcode *"
                                                    value={formik?.values?.zipCode}
                                                    disabled={!isSettingWrite}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('zipCode', text)
                                                    }}
                                                    onBlur={() => { formik.setFieldTouched('zipCode', true); formik?.setFieldValue('zipCode', String(formik?.values?.zipCode)?.trim()) }}
                                                    errorMessage={(formik?.touched.zipCode && formik?.errors.zipCode) ? formik?.errors.zipCode : null}
                                                    errorStyle={{ fontFamily: theme.medium, fontSize: theme.tinyFontSize, color: theme.red, marginLeft: 0 }}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                        </View>
                                    </View>
                                    {/* Social Media Details */}
                                    <View style={[theme?.addEditCustomerStyle?.sectionView, { marginHorizontal: width > 600 ? 15 : 10 }]}>
                                        <TouchableOpacity style={[{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", backgroundColor: '#fbfbfb', padding: 10 }, collapseHeader?.indexOf("Social Media Details") < 0 ? { borderRadius: 10 } : { borderTopLeftRadius: 10, borderTopRightRadius: 10 }]} onPress={() => { collapseFunction("Social Media Details") }}>
                                            <View style={{ flexDirection: "row", alignItems: "center", marginLeft: 5 }}>
                                                <ZenIcon type="material-community" name={collapseHeader?.indexOf("Social Media Details") < 0 ? "chevron-up" : "chevron-down"} size={26} color={theme?.primary} />
                                                <ZenText content={"Social Media Details"} size={width > 600 ? "medium" : theme?.smallerFontSize} color={theme.primary} bold style={{ marginHorizontal: 5 }} />
                                            </View>
                                        </TouchableOpacity>
                                        <View style={[theme?.addEditCustomerStyle?.generalSectionView, { justifyContent: "space-between", alignItems: "flex-start", paddingHorizontal: width > 600 ? 10 : 5, display: collapseHeader?.indexOf("Social Media Details") < 0 ? "none" : "flex" }]}>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Facebook"
                                                    value={formik?.values?.faceBook}
                                                    disabled={!isSettingWrite}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('faceBook', text);
                                                    }}
                                                    onBlur={formik?.handleBlur('faceBook')}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    errorMessage={formik?.touched.faceBook && formik?.errors.faceBook ? formik?.touched.faceBook && formik?.errors.faceBook : null}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Instagram"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.instagram}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('instagram', text);
                                                    }}
                                                    onBlur={formik?.handleBlur('instagram')}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    errorMessage={formik?.touched.instagram && formik?.errors.instagram ? formik?.touched.instagram && formik?.errors.instagram : null}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Linkedin"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.linkedIn}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('linkedIn', text);
                                                    }}
                                                    onBlur={formik?.handleBlur('linkedIn')}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    errorMessage={formik?.touched.linkedIn && formik?.errors.linkedIn ? formik?.touched.linkedIn && formik?.errors.linkedIn : null}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Pinterest"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.pinterest}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('pinterest', text);
                                                    }}
                                                    onBlur={formik?.handleBlur('pinterest')}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    errorMessage={formik?.touched.pinterest && formik?.errors.pinterest ? formik?.touched.pinterest && formik?.errors.pinterest : null}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Twitter"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.twitter}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('twitter', text);
                                                    }}
                                                    onBlur={formik?.handleBlur('twitter')}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    errorMessage={formik?.touched.twitter && formik?.errors.twitter ? formik?.touched.twitter && formik?.errors.twitter : null}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                            <View style={{ width: inputWidth }}>
                                                <ZenInput
                                                    label="Youtube"
                                                    disabled={!isSettingWrite}
                                                    value={formik?.values?.youtube}
                                                    onChangeText={(text: string) => {
                                                        formik?.setFieldValue('youtube', text);
                                                    }}
                                                    onBlur={formik?.handleBlur('youtube')}
                                                    errorStyle={{ fontFamily: theme.medium, color: theme.red, fontSize: theme.errorFontSize }}
                                                    errorMessage={formik?.touched.youtube && formik?.errors.youtube ? formik?.touched.youtube && formik?.errors.youtube : null}
                                                    labelStyle={theme?.addEditCustomerStyle?.labelStyle}
                                                    inputStyle={theme?.modifierSetStyle?.inputStyle}
                                                    type="outlined"
                                                    containerStyle={{ paddingHorizontal: 15, }}
                                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                                />
                                            </View>
                                        </View>
                                    </View>
                                </ScrollView>
                                <CommonImageUpload
                                    bottomRef={bottomRef}
                                    propFn={handleImage}
                                    gallery={{ isStoreGallery: false }}
                                    selectedIndex={{ value: { imagesMaxLength: 1 } }}
                                />
                            </View>
                    }
                    <CustomModal visible={transparentLoader} transparent>
                        <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                    </CustomModal>
                    {/* For leave confirmation */}
                    <Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
                        <CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
                    </Popover>
                    {/* For remove image confirmation */}
                    <Popover isVisible={removeImagePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
                        <CommonDialogBox replaceValue="logo" type="DELETE" confirmation={removeImageConfirmation} />
                    </Popover>
                </KeyboardAvoidingView >
            )
            }
        </ThemeConsumer >
    )
}
export default StoreSetting;